import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Person as UserIcon,
} from '@mui/icons-material';

const navigationItems = [
  { text: 'Dashboard', icon: <UserIcon />, path: '/dashboard' },
  { text: 'Content Generation', icon: <UserIcon />, path: '/generate' },
  { text: 'Generation History', icon: <UserIcon />, path: '/history' },
  { text: 'Business Profile', icon: <UserIcon />, path: '/business-profiles' },
  { text: 'Billing', icon: <UserIcon />, path: '/billing' },
  { text: 'User Management', icon: <UserIcon />, path: '/admin/users', adminOnly: true },
];

interface UserHeaderProps {
  user: any;
  anchorEl: HTMLElement | null;
  onProfileMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
  onProfileMenuClose: () => void;
  onLogout: () => void;
  onProfileSettings: () => void;
  onDrawerToggle: () => void;
  onDesktopDrawerToggle?: () => void;
  desktopOpen: boolean;
  location: any;
}

const UserHeader: React.FC<UserHeaderProps> = ({
  user,
  anchorEl,
  onProfileMenuOpen,
  onProfileMenuClose,
  onLogout,
  onProfileSettings,
  onDrawerToggle,
  onDesktopDrawerToggle,
  desktopOpen,
  location,
}) => {

  const getCurrentPageTitle = () => {
    const currentItem = navigationItems.find(item => 
      item.path === location.pathname || 
      (item.path !== '/dashboard' && location.pathname.startsWith(item.path))
    );
    return currentItem?.text || 'Dashboard';
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { sm: desktopOpen ? `calc(100% - 280px)` : '100%' },
        ml: { sm: desktopOpen ? `280px` : '0px' },
        background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        minHeight: 78, // Adjust this value to change the AppBar height
        transition: 'width 0.3s ease-in-out, margin-left 0.3s ease-in-out',
      }}
    >
      <Toolbar sx={{ minHeight: 78 }}> {/* Adjust this value to match AppBar height */}
        <IconButton
          color="inherit"
          aria-label="open drawer"
          edge="start"
          onClick={onDrawerToggle}
          sx={{ 
            mr: 2, 
            display: { sm: 'none' },
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' },
          }}
        >
          <MenuIcon />
        </IconButton>
        {onDesktopDrawerToggle && (
          <IconButton
            color="inherit"
            aria-label="toggle sidebar"
            edge="start"
            onClick={onDesktopDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { xs: 'none', sm: desktopOpen ? 'none' : 'flex' },
              color: 'white',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)', color: 'white' },
            }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexGrow: 1 }}>
          <Typography 
            variant="h5" 
            noWrap 
            component="div" 
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              letterSpacing: '-0.02em',
              color: 'white',
            }}
          >
            {getCurrentPageTitle()}
          </Typography>
          
          {/* User Profile Menu */}
          <IconButton
            size="large"
            edge="end"
            aria-label="account of current user"
            aria-controls="primary-search-account-menu"
            aria-haspopup="true"
            onClick={onProfileMenuOpen}
            color="inherit"
            sx={{ 
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <Avatar sx={{ width: 36, height: 36, bgcolor: 'rgba(255,255,255,0.25)', fontWeight: 600 }}>
              {user?.first_name?.charAt(0) || user?.email?.charAt(0) || 'U'}
            </Avatar>
          </IconButton>
        </Box>
      </Toolbar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onProfileMenuClose}
        onClick={onProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={onProfileSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile Settings</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem onClick={onLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </AppBar>
  );
};

export default UserHeader;

